import EditIcon from "@mui/icons-material/Edit";
import UndoIcon from "@mui/icons-material/Undo";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { SortingState } from "@tanstack/react-table";
import TableContainer2 from "Common/Tables/ObjectFiltersTable/TableContainer2";
import LoaderErrorWrapper from "components/LoaderErrorWrapper/LoaderErrorWrapper";
import {
  DynamicMenuItem,
  MenuItemConfig,
} from "components/Menuitem/DynamicMenuItem";
import TruncatedTextCell from "components/TruncatedTextCell/TruncatedTextCell";
import { InwardType, normalizeInwardType } from "Constants/inwardTypes";
import usePermission from "CustomHooks/usePermission";
import { app_modules } from "helpers/apps_modules_list";
import { useEffect, useMemo } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { createSelector } from "reselect";
import {
  checkGRNEditable,
  checkGRNRevertable,
  fetchGRNList,
  revertGRN,
  revertOrderReturnGRN,
  revertStockTransferInward,
} from "slices/Warehouse/ItemInward2/GRN/thunk";
import { revertLabourInward } from "slices/Warehouse/ItemInward2/PendingLabourInward/thunk";
import { showConfirm } from "utils/swalHelper";

type GRNProps = {
  loading: boolean;
  pageIndex: number;
  pageSize: number;
  sorting: SortingState;
  columnFilters: any[];
  filterValue: any[];
  search: string;
  searchInput: string;
  warehouseIdSearch: string;
  sortField: string;
  sortDirection: string;
  setSearchInput: React.Dispatch<React.SetStateAction<string>>;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  setPageIndex: React.Dispatch<React.SetStateAction<number>>;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  setSorting: React.Dispatch<React.SetStateAction<SortingState>>;
  setColumnFilters: React.Dispatch<React.SetStateAction<any[]>>;
  setFilterValue: React.Dispatch<React.SetStateAction<any[]>>;
  setWarehouseIdSearch: React.Dispatch<React.SetStateAction<string>>;
};

const GRN: React.FC<GRNProps> = ({
  loading,
  pageIndex,
  pageSize,
  sorting,
  columnFilters,
  filterValue,
  search,
  searchInput,
  warehouseIdSearch,
  sortField,
  sortDirection,
  setSearch,
  setSearchInput,
  setPageIndex,
  setPageSize,
  setSorting,
  setColumnFilters,
  setFilterValue,
  setWarehouseIdSearch,
}) => {
  document.title = "GRN";
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  // Redux selectors
  const selectGRN = createSelector(
    (state: any) => state.grn,
    (item) => ({
      list: item.list,
      rowCount: item.rowCount,
      loading: item.loading,
      error: item.error,
    })
  );

  const {
    list,
    rowCount,
    loading: tableLoading,
    error,
  } = useSelector(selectGRN);

  // Use custom hook to fetch and access permissions
  const { canView, canUpdate } = usePermission(
    app_modules?.warehouse?.app_name,
    app_modules?.warehouse?.modules?.item_inward
  );

  const data = list || [];

  // Fetch GRN list
  useEffect(() => {
    dispatch(
      fetchGRNList({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? (sortDirection as "asc" | "desc") : "",
        search,
        filters: filterValue,
        warehouse_id: warehouseIdSearch || "",
      })
    );
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sortField,
    sortDirection,
    search,
    filterValue,
    warehouseIdSearch,
  ]);

  // Create menu items for DynamicMenuItem
  const menuItems: MenuItemConfig[] = useMemo(() => {
    const items: MenuItemConfig[] = [
      {
        icon: EditIcon,
        label: "Edit",
       permission: (grn: any) => {
          // Hide edit button for stock transfer GRNs
          const normalizedType = normalizeInwardType(grn.inward_type);
          if (normalizedType === InwardType.STOCK_TRANSFER) {
            return false;
          }
          return !!canUpdate;
        },
        onClick: async (grn: any) => {
          try {
            // Check if GRN can be edited before navigating
            const result = await dispatch(
              checkGRNEditable(grn.inventory_id)
            ).unwrap();

            // If GRN cannot be edited (items are consumed), show toast and return
            if (!result.can_edit) {
              toast.error(
                "This GRN cannot be edited as inventory items are consumed."
              );
              return;
            }

            // Navigate to edit page based on inward type
            const normalizedType = normalizeInwardType(grn.inward_type);
            if (normalizedType === InwardType.SUPPLIER_INWARD) {
              navigate(`/warehouse-item-inward-create-grn/${grn.inventory_id}`);
            } else if (normalizedType === InwardType.STOCK_TRANSFER) {
              navigate(
                `/warehouse-stock-transfer-item-inward-edit-grn/${grn.inventory_id}`
              );
            } else if (normalizedType === InwardType.LABOUR_INWARD) {
              navigate(
                `/warehouse-labour-item-inward-grn-edit/${grn.inventory_id}`
              );
            } else if (normalizedType === InwardType.ORDER_RETURN) {
              navigate(
                `/warehouse-sales-return-edit-item-inward-grn/${grn.inventory_id}`
              );
            } else {
              toast.info(
                "Edit functionality not available for this inward type"
              );
            }
          } catch (error: any) {
            console.error("Error checking GRN editability:", error);
            toast.error(
              error?.message || "Failed to check if GRN can be edited"
            );
          }
        },
        color: "themeColor",
      },
      {
        icon: VisibilityIcon,
        label: "Details",
        permission: canView,
        onClick: (grn: any) => {
          navigate(`/warehouse-grn-details/${grn.inventory_id}`, {
            state: { type: grn.inward_type },
          });
        },
        color: "themeColor",
      },
      {
        icon: UndoIcon,
        label: "Revert",
        permission: canUpdate,
        onClick: async (grn: any) => {
          try {
            // Check if GRN can be reverted
            const result = await dispatch(
              checkGRNRevertable(grn.inventory_id)
            ).unwrap();

            // If GRN cannot be reverted, show error and prevent revert
            if (!result.can_revert) {
              const reason =
                result.reason || "inventory items are consumed or reserved";
              toast.error(`This GRN cannot be reverted: ${reason}`);
              return;
            }

            // Show confirmation dialog for revert
            showConfirm(
              "Do you want to revert this GRN? This will delete the GRN and all associated items.",
              "Are you sure?",
              "Yes",
              "No"
            ).then(async (swalResult) => {
              if (swalResult.isConfirmed) {
                try {
                  // Call revert API
                  const normalizedType = normalizeInwardType(grn.inward_type);
                  if (normalizedType === InwardType.ORDER_RETURN) {
                    await dispatch(
                      revertOrderReturnGRN(grn.inventory_id)
                    ).unwrap();
                    toast.success("GRN reverted successfully");
                  } else if (normalizedType === InwardType.LABOUR_INWARD) {
                    await dispatch(
                      revertLabourInward(grn.inventory_id)
                    ).unwrap();
                    toast.success("Labour GRN reverted successfully");
                  } else if (normalizedType === InwardType.STOCK_TRANSFER) {
                    await dispatch(
                      revertStockTransferInward(grn.inventory_id)
                    ).unwrap();
                    toast.success(
                      "Stock transfer inward reverted successfully"
                    );
                  } else {
                    await dispatch(revertGRN(grn.inventory_id)).unwrap();
                    toast.success("GRN reverted successfully");
                  }
                  // Refresh the GRN list
                  dispatch(
                    fetchGRNList({
                      page: pageIndex + 1,
                      pageSize,
                      sort: sortField,
                      order: sortField ? (sortDirection as "asc" | "desc") : "",
                      search,
                      filters: filterValue,
                      warehouse_id: warehouseIdSearch || "",
                    })
                  );
                } catch (error: any) {
                  console.error("Error reverting GRN:", error);
                  const errorMessage =
                    error?.response?.data?.message ||
                    error?.message ||
                    "Failed to revert GRN";
                  toast.error(errorMessage);
                }
              }
            });
          } catch (error: any) {
            console.error("Error checking GRN revertability:", error);
            toast.error(
              error?.message || "Failed to check if GRN can be reverted"
            );
          }
        },
        color: "error",
      },
    ];
    return items;
  }, [
    canView,
    canUpdate,
    navigate,
    dispatch,
    pageIndex,
    pageSize,
    sortField,
    sortDirection,
    search,
    filterValue,
    warehouseIdSearch,
  ]);

  const DynamicAction = useMemo(() => {
    // Only show Action column if user has at least one permission
    const hasPermission = canUpdate || canView;

    if (!hasPermission) return [];

    return [
      {
        header: "Actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => (
          <DynamicMenuItem row={row.original} menuItems={menuItems} />
        ),
      },
    ];
  }, [canUpdate, canView, menuItems]);

  const columns = useMemo(
    () => [
      {
        header: "Sr No.",
        accessorKey: "sr_no",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row, table }: { row: any; table: any }) => {
          return (
            row.original.sr_no || row.index + 1 + pageIndex * pageSize || "-"
          );
        },
      },
      {
        header: "GRN No.",
        accessorKey: "grn_no",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `/warehouse/item-inward/dropdown/grn_no/?warehouse_id=${warehouseIdSearch}`,
        cell: ({ row }: { row: any }) => row.original.grn_no || "-",
      },
      {
        header: "Inward Type",
        accessorKey: "inward_type",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `/warehouse/item-inward/dropdown/inward_type/?warehouse_id=${warehouseIdSearch}`,
        cell: ({ row }: { row: any }) =>
          normalizeInwardType(row.original.inward_type) || "-",
      },
      {
        header: "Inward Date",
        accessorKey: "inward_date",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const date = row.original.inward_date
            ? new Date(row.original.inward_date).toISOString().split("T")[0]
            : "N/A";
          return `${date}`;
        },
      },
      {
        header: "Received By",
        accessorKey: "received_by",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `/warehouse/item-inward/dropdown/received_by/?warehouse_id=${warehouseIdSearch}`,
        cell: ({ row }: { row: any }) => {
          const receivedByFirstName =
            row.original.received_by_details?.first_name || "-";
          const receivedByLastName =
            row.original.received_by_details?.last_name || "-";
          return (
            <div
              style={{
                whiteSpace: "normal",
                wordWrap: "break-word",
                maxWidth: "250px",
              }}
            >
              {receivedByFirstName} {receivedByLastName}
            </div>
          );
        },
      },
      {
        header: "Remarks",
        accessorKey: "remarks",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => (
          <TruncatedTextCell
            text={row?.original?.remarks}
            maxWidth="300px"
            maxLines={4}
            rowIndex={row.index}
            tooltipId={`remarks-tooltip-${row.index}`}
          />
        ),
      },
      {
        header: "Created By/Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        filterType: "date" as const,
        enableSorting: true,
        cell: ({ row }: { row: any }) => {
          const name = row.original.created_by_user?.username || "-";
          const date = row.original.created_at
            ? new Date(row.original.created_at).toISOString().split("T")[0]
            : "-";
          return `${name} - ${date}`;
        },
      },
      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        filterType: "date" as const,
        enableSorting: true,
        cell: ({ row }: { row: any }) => {
          const name = row.original.updated_by_user?.username || "-";
          const date = row.original.updated_at
            ? new Date(row.original.updated_at).toISOString().split("T")[0]
            : "-";
          return `${name} - ${date}`;
        },
      },
      ...DynamicAction,
    ],
    [pageIndex, pageSize, warehouseIdSearch, DynamicAction]
  );

  return (
    <>
      <style>
        {`
          .truncated-text-cell-wrapper {
            min-width: 0 !important;
            display: -webkit-box !important;
            -webkit-box-orient: vertical !important;
            overflow: hidden !important;
            overflow-wrap: anywhere !important;
            word-break: break-word !important;
            white-space: normal !important;
            line-height: 1.5em !important;
          }
        `}
      </style>
      <Row>
        <Col lg={12}>
          <Card>
          <Card.Body className="mt-3">
            <LoaderErrorWrapper loading={loading || tableLoading} error={error}>
              <TableContainer2
                columns={columns}
                data={data}
                totalRecords={rowCount}
                pageIndex={pageIndex}
                pageSize={pageSize}
                onPageChange={setPageIndex}
                onPageSizeChange={setPageSize}
                columnFilters={columnFilters}
                setColumnFilters={setColumnFilters}
                colValue={setFilterValue}
                sorting={sorting}
                onSortingChange={setSorting}
                isBordered={false}
                isPagination={true}
                isGlobalFilter={false}
                SearchPlaceholder="Search..."
                manualSorting={true}
              />
            </LoaderErrorWrapper>
          </Card.Body>
        </Card>
      </Col>
    </Row>
    </>
  );
};

export default GRN;
